import React, { useState } from 'react';
import {
    Box,
    Card,
    IconButton,
    Stack,
    Typography,
    Collapse,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    type SelectChangeEvent,
} from '@mui/material';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import { IAudition, IRole } from '../../types/global/helper';
import CustomButton from '../Button/CustomButton';
import { CheckCircleOutline, ExpandMore } from '@mui/icons-material';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { AuditionList } from './AuditionList';
import { LinkBox } from '../LinkBox';
import { generateShareLink } from '../../utils/AppUtils';
import { useNavigate } from 'react-router-dom';
import { CompanyCard } from '../Cards/CompanyCard';
import { AUDITION_FIELD_ACCENT } from './auditionFieldTheme';
import { useDispatch } from 'react-redux';
import { setRole } from '../../redux/audition/actions';
import { useAuth } from '../../hook/useAuth';
import moment from 'moment';
import { MixPanel, MIXPANEL_EVENT } from '../../analytics/Mixpanel';

export interface IAuditionSuggestion {
    audition: IAudition;
    disableSuggestion?: boolean;
    similarAuditions?: IAudition[];
    hideDetails?: boolean;
    /** Optional hook after role is chosen (e.g. analytics); Redux `setRole` opens apply modal in layout. */
    onApplyClick?: (role: IRole) => void;
    /**
     * Fixed footer on small screens: full-bleed width, anchored to bottom of dynamic viewport.
     * Hides tablet “Get shareable link” UI; share stays on desktop / tablet in-column layout.
     */
    mobileDocked?: boolean;
}

const ROLE_SELECT_LABEL_ID = 'audition-suggestion-role-label';

const AuditionSuggestion: React.FC<IAuditionSuggestion> = ({
    audition,
    disableSuggestion,
    similarAuditions,
    hideDetails,
    onApplyClick,
    mobileDocked = false,
}) => {
    const { themeStyle, device } = useThemeContext();
    const dispatch = useDispatch();
    const { user_id } = useAuth();
    const [selectedRoleId, setSelectedRoleId] = useState('');
    const [linkBoxExpanded, setLinkBoxExpanded] = useState(false);
    const navigate = useNavigate();
    const showTabletShareLink = !device.isDesktop && !mobileDocked;

    const { roles: auditionRoles } = audition;
    const roles = auditionRoles.filter((role): role is IRole => role != null);
    const isExternalUser = user_id !== audition.created_by?._id;
    const castingClosed = !!audition.casting_expiration && moment(audition.casting_expiration).isBefore(moment());
    const selectedRole = roles.find(r => r._id === selectedRoleId);

    const toggleLinkBox = () => {
        setLinkBoxExpanded(!linkBoxExpanded);
    };

    const handleRoleChange = (e: SelectChangeEvent<string>) => {
        const roleId = e.target.value;
        const nextRole = roles.find(r => r._id === roleId);
        MixPanel.track(MIXPANEL_EVENT.SELECT_APPLY_ROLE_CLICK, {
            buttonName: String(nextRole?.character ?? 'ROLE').toUpperCase(),
            buttonAction: 'SELECT_APPLY_ROLE',
            roleId,
            roleName: nextRole?.character,
            auditionId: audition._id,
            auditionSlug: audition.slug,
        });
        setSelectedRoleId(roleId);
    };

    const handleApplyClick = () => {
        if (!selectedRole) return;
        MixPanel.track(MIXPANEL_EVENT.APPLY_TO_ROLE_CLICK, {
            buttonName: 'APPLY',
            buttonAction: 'APPLY_TO_ROLE',
            roleId: selectedRole._id,
            roleName: selectedRole.character,
            auditionId: audition._id,
            auditionSlug: audition.slug,
            source: 'ready_to_apply',
        });
        dispatch(setRole(selectedRole));
        onApplyClick?.(selectedRole);
    };

    const selectSx = {
        mt: 0.5,
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255,255,255,0.12)',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255,255,255,0.2)',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: AUDITION_FIELD_ACCENT,
        },
        '& .MuiSelect-select': {
            fontFamily: FONT_ENUM.MAIN_MEDIUM,
            fontSize: 14,
            color: theme[themeStyle].tag_white,
        },
        '& .MuiSvgIcon-root': { color: theme[themeStyle].light_gray_text },
    };

    const readyBody = (
        <Stack spacing={2} sx={{ pt: 1 }}>
            <Box>
                <Typography
                    sx={{
                        fontSize: 22,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        color: theme[themeStyle].tag_white,
                        fontWeight: 800,
                    }}>
                    Ready to Apply?
                </Typography>
                <Typography
                    sx={{
                        mt: 1,
                        fontSize: 13,
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        color: theme[themeStyle].light_gray_text,
                        lineHeight: 1.5,
                    }}>
                    Submit your portfolio and profile for consideration by the production team. Choose a role below and
                    tap Apply.
                </Typography>
                {roles.length > 0 && (
                    <FormControl fullWidth sx={{ mt: 2 }} size="small">
                        <InputLabel
                            id={ROLE_SELECT_LABEL_ID}
                            shrink
                            sx={{
                                color: theme[themeStyle].light_gray_text,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 12,
                                letterSpacing: '0.08em',
                                textTransform: 'uppercase',
                                '&.Mui-focused': { color: AUDITION_FIELD_ACCENT },
                            }}>
                            Role
                        </InputLabel>
                        <Select
                            labelId={ROLE_SELECT_LABEL_ID}
                            label="Role"
                            value={selectedRoleId}
                            onChange={handleRoleChange}
                            sx={selectSx}
                            displayEmpty
                            notched
                            renderValue={value => {
                                if (!value) {
                                    return (
                                        <Typography component="span" sx={{ opacity: 0.55, fontSize: 14 }}>
                                            Choose a role
                                        </Typography>
                                    );
                                }
                                return roles.find(r => r._id === value)?.character ?? '';
                            }}
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        bgcolor: themeStyle === 'dark' ? '#2a2a2a' : theme[themeStyle].secondaryBg,
                                        color: theme[themeStyle].tag_white,
                                    },
                                },
                            }}>
                            <MenuItem value="">
                                <em>Choose a role</em>
                            </MenuItem>
                            {roles.map(role => (
                                <MenuItem key={role._id} value={role._id} sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                                    {role.character}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
                {isExternalUser && roles.length > 0 && (
                    <CustomButton
                        label={castingClosed ? 'Closed' : 'Apply'}
                        sx={{ width: '100%', mt: 1.5 }}
                        disabled={!selectedRole || castingClosed}
                        withAuthentication
                        onClick={handleApplyClick}
                    />
                )}
            </Box>
        </Stack>
    );

    const shareBlock = (
        <Box sx={{ pt: 2, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <Typography
                sx={{
                    fontSize: 11,
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    color: theme[themeStyle].light_gray_text,
                    textTransform: 'uppercase',
                    letterSpacing: '0.2em',
                    mb: 2,
                }}>
                Share Link
            </Typography>
            <LinkBox link={generateShareLink('audition', audition, true)} />
        </Box>
    );

    const viewMoreBtn = !hideDetails && (
        <CustomButton
            label="View more details"
            onClick={() => navigate(`/audme/${audition.slug}`)}
            customStyle={{ marginTop: '16px', width: '100%', borderRadius: '999px' }}
            sx={{
                backgroundColor: AUDITION_FIELD_ACCENT,
                border: 'none',
                '&:hover': {
                    WebkitTextFillColor: theme[themeStyle].true_white,
                    backgroundColor: AUDITION_FIELD_ACCENT,
                    opacity: 0.92,
                },
            }}
        />
    );

    return (
        <React.Fragment>
            <Stack direction="column">
                <Card
                    sx={{
                        width: '100%',
                        minWidth: mobileDocked ? 0 : { sm: 'min(100%, 350px)' },
                        borderRadius: mobileDocked
                            ? '16px 16px 0 0'
                            : {
                                  xs: '2rem',
                                  sm: '2rem',
                              },
                        overflow: 'visible',
                        border: '1px solid rgba(255,255,255,0.06)',
                        borderBottom: mobileDocked ? 'none' : '1px solid rgba(255,255,255,0.06)',
                        my: mobileDocked ? 0 : { xs: 0, sm: 2 },
                        mx: mobileDocked ? 0 : undefined,
                        ...(mobileDocked ? { p: 2, pt: 2.5 } : { p: 3 }),
                        position: 'relative',
                        backgroundColor: themeStyle === 'dark' ? '#20201f' : theme[themeStyle].secondaryBg,
                        boxShadow: mobileDocked ? '0 -12px 40px rgba(0,0,0,0.45)' : undefined,
                    }}>
                    <IconButton
                        sx={{
                            position: 'absolute',
                            top: 0,
                            right: mobileDocked ? 12 : 16,
                            borderRadius: 0,
                            borderBottomLeftRadius: '50%',
                            borderBottomRightRadius: '50%',
                            p: 0.5,
                            backgroundColor: AUDITION_FIELD_ACCENT,
                        }}>
                        <CheckCircleOutline sx={{ color: theme[themeStyle].true_white, fontSize: 24 }} />
                    </IconButton>
                    {readyBody}
                    {device.isDesktop && (
                        <>
                            {shareBlock}
                            {viewMoreBtn}
                        </>
                    )}
                    {mobileDocked && viewMoreBtn}
                </Card>
                {!disableSuggestion && Array.isArray(similarAuditions) && similarAuditions.length > 0 && (
                    <Card
                        sx={{
                            width: '100%',
                            borderRadius: 2,
                            overflow: 'visible',
                            p: 0,
                            minHeight: 'fit-content',
                            backgroundColor: theme[themeStyle].secondaryBg,
                        }}>
                        <Box borderBottom={1}>
                            <Box px={2} py={1}>
                                <Typography
                                    style={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: 19,
                                        color: theme[themeStyle].tag_white,
                                    }}>
                                    Similar Calls
                                </Typography>
                            </Box>
                        </Box>
                        <AuditionList auditions={similarAuditions!} />
                    </Card>
                )}
                {showTabletShareLink && (
                    <>
                        <Box sx={{ mt: 2 }}>
                            <CustomButton
                                label="Get shareable link"
                                onClick={toggleLinkBox}
                                buttonProps={{ variant: 'text' }}
                                customStyle={{
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '10px 16px',
                                }}
                                withIcon
                                customIcon={
                                    <ExpandMore
                                        sx={{
                                            transform: linkBoxExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                            transition: 'transform 0.3s ease',
                                            color: theme[themeStyle].tag_white,
                                        }}
                                    />
                                }
                            />
                            <Collapse in={linkBoxExpanded} timeout="auto" unmountOnExit>
                                <Box sx={{ mt: 1 }}>
                                    <LinkBox link={generateShareLink('audition', audition, true)} />
                                </Box>
                            </Collapse>
                        </Box>
                        {viewMoreBtn}
                    </>
                )}
                {hideDetails && device.isDesktop && audition.production_company && (
                    <CompanyCard data={audition.production_company} enableHeader />
                )}
            </Stack>
        </React.Fragment>
    );
};

export { AuditionSuggestion };
